import { IShuttleAddedJourney } from "../../Orchestrator/Types/Interface/IShuttleBus";

/**
 * The leg's riders who hold NO shuttle on this side yet.
 *
 * This is what the "Book" action sells to: a booking of three whose shuttle was
 * bought for two leaves one rider here, and booking for them creates a SECOND
 * offer on the side rather than replacing the paid one (which is what
 * ONWARD_CHANGE_ENABLED refuses to do).
 *
 * Returns NULL to REFUSE (coverage unreadable) and [] for "everyone covered" —
 * see the guard below; callers must not collapse the two.
 *
 * `passengers` must already be the shuttle-eligible set — `shuttleOfferPassengers`
 * drops infants, who share a guardian's place and ride free, so they are not
 * "uncovered" and must never appear as a rider to buy for.
 */
export function uncoveredShuttleRefs(
  passengers: { externalRef?: string }[],
  ownedEntries: IShuttleAddedJourney[] | undefined
): string[] | null {
  const entries = ownedEntries || [];
  // An owned offer whose riders cannot be read covers an UNKNOWN set, not an
  // empty one. Offering the whole party there would sell a second shuttle to
  // someone who already holds one — money taken for a duplicate. Refuse the
  // whole action instead, exactly as the refund step refuses when it cannot
  // name everyone it would touch.
  //
  // NULL, not []. They are opposite instructions: [] means "everyone is already
  // covered, offer nothing extra", which callers legitimately answer by falling
  // back to a plain whole-party add. Returning that here would answer a refusal
  // with the largest possible sale.
  if (entries.some((e) => !(e?.passengerRefs || []).length)) return null;

  const covered = new Set(entries.flatMap((e) => e.passengerRefs || []));
  return passengers
    .map((p) => p?.externalRef)
    .filter((ref): ref is string => !!ref && !covered.has(ref));
}
